import { Outlet } from 'react-router-dom';

import { PageRobotRoutineProvider } from '@sbrc/contexts';
import { useAuthentication, useFeatureFlag } from '@sbrc/hooks';

import { RoutineAgentChatMount } from '../chat/RoutineAgentChatMount';
import { RestoreConfirmationMount } from '../restore';
import { RosApiRestartBannerMount } from '../service-restart/RosApiRestartBannerMount';

/**
 * Router-root layout. Mounts the chat panel (and its routine
 * context) exactly once for the lifetime of the authenticated
 * session, so navigating between routes leaves the panel — its
 * provider state, message list, and `/events` subscription — fully
 * intact. Without this, every per-page `<PrivatePage>` instance
 * would re-mount the chat on every route swap and the conversation
 * would reset.
 *
 * The `PrivatePage` component still owns per-page authentication
 * redirects (logout, mustChangePin) and its own page chrome; this
 * layout is purely about anchoring the chat above the route swap.
 *
 * Gates:
 * - No chat if the user isn't authenticated (avoids showing it on
 *   `/login`, `/privacy`, etc.).
 * - No chat unless both `appRedesign` AND `routineAgentChat` flags
 *   are on.
 *
 * It also anchors the restore interlock, for the same reason and one more:
 * a cloud-ordered restore erases the robot, so its confirmation has to
 * reach the operator on whatever page they are on, not only if they happen
 * to open settings. It renders nothing unless a restore is actually
 * pending, and is gated on authentication so a login screen never shows it.
 */
export default function RootLayout() {
  const { userID } = useAuthentication();
  const isRedesign = useFeatureFlag('appRedesign');
  const isRoutineAgentChatEnabled = useFeatureFlag('routineAgentChat');
  const isAuthed = userID !== null && userID !== undefined;
  const showChat = isAuthed && isRedesign && isRoutineAgentChatEnabled;

  if (!isAuthed) {
    return <Outlet />;
  }

  if (!showChat) {
    return (
      <PageRobotRoutineProvider>
        <Outlet />
        <RestoreConfirmationMount />
        <RosApiRestartBannerMount />
      </PageRobotRoutineProvider>
    );
  }

  return (
    <PageRobotRoutineProvider>
      <RoutineAgentChatMount>
        <Outlet />
      </RoutineAgentChatMount>
      <RestoreConfirmationMount />
      <RosApiRestartBannerMount />
    </PageRobotRoutineProvider>
  );
}
